import { When } from 'react-if';

import { isNotEmpty } from '@realwork/shared';

import { JobCards } from './JobCards';
import { OverviewSection } from './OverviewSection';
import { useContentLists } from '../../fetchers';
import { NeighborhoodStatus, useMapContext, usePostCode } from '../../hooks';
import {
    DetailsMode,
    JobListProvider,
    useBreakpoint,
    useContentDetails,
} from '../../providers';
import {
    AnalyticsTargetLayerAreaProvider,
    createOrGetIntersectionContext,
    getPageUrl,
    ReviewCarousel,
    ReviewCarouselProvider,
    useAnalyticsClickEvent,
    useAnalyticsViewEvent,
    useIntersectionTarget,
} from 'rwl-ui';
import { MiniGoogleAggregate } from '../Header/GoogleAggregate';
import {
    ClickTargets,
    TargetLayerArea,
    ViewTargets,
} from 'rwl-shared/dist/models/analytics';
import { useCallback, useEffect, useState } from 'react';

const MINIMUM_FEATURED_CONTENT = 5;

export const FeaturedContentLists = () => (
    <>
        <AnalyticsTargetLayerAreaProvider
            targetLayerArea={TargetLayerArea.REVIEWS}
        >
            <Reviews />
        </AnalyticsTargetLayerAreaProvider>
        <AnalyticsTargetLayerAreaProvider
            targetLayerArea={TargetLayerArea.JOBS}
        >
            <Jobs />
        </AnalyticsTargetLayerAreaProvider>
    </>
);

const Reviews = () => {
    const postCode = usePostCode();
    const { allReviewsList, isLoading } = useContentLists();
    const { openDetails } = useContentDetails();
    const { isBelowBreakpoint } = useBreakpoint();
    const sendViewEvent = useAnalyticsViewEvent();
    const sendClickEvent = useAnalyticsClickEvent();
    const { neighborhoodStatus } = useMapContext();

    const handleOnSelectChange = useCallback(
        (selectedIndex: number, numberInView: number) => {
            if (allReviewsList.length === 0) {
                return;
            }

            const reviewsInView = allReviewsList.slice(
                selectedIndex,
                selectedIndex + numberInView
            );
            reviewsInView.forEach((review) => {
                sendViewEvent({
                    url: getPageUrl(),
                    target: ViewTargets.REVIEW_SUMMARY,
                    content: review.id,
                });
            });
        },
        [allReviewsList]
    );

    const handleOnNextClick = () => {
        sendClickEvent({
            url: getPageUrl(),
            target: ClickTargets.NEXT,
        });
    };

    const handleOnPrevClick = () => {
        sendClickEvent({
            url: getPageUrl(),
            target: ClickTargets.PREV,
        });
    };

    const handleOnCardClick = (id: string) => {
        const review = allReviewsList.find((r) => r.id === id);
        openDetails(
            review?.jobId,
            review?.id,
            allReviewsList,
            DetailsMode.REVIEW
        );
        sendClickEvent({
            url: getPageUrl(),
            target: ClickTargets.REVIEW,
            content: id,
        });
    };

    return (
        <When condition={isNotEmpty(allReviewsList)}>
            <OverviewSection
                style={{
                    flex: 0,
                    position: 'relative',
                }}
                title={`Featured reviews${
                    postCode &&
                    neighborhoodStatus === NeighborhoodStatus.FOUND &&
                    isNotEmpty(allReviewsList)
                        ? ' in ' + postCode
                        : ''
                }`}
                titleStyle={{
                    position: isBelowBreakpoint('xs') ? 'relative' : 'absolute',
                    marginRight: 0,
                    marginLeft: 0,
                }}
            >
                <ReviewCarouselProvider
                    reviews={allReviewsList}
                    isLoading={isLoading}
                >
                    <ReviewCarousel
                        onSelectChange={handleOnSelectChange}
                        onNextClick={handleOnNextClick}
                        onPrevClick={handleOnPrevClick}
                        onCardClick={handleOnCardClick}
                    />
                </ReviewCarouselProvider>
                <When condition={isBelowBreakpoint('sm')}>
                    <MiniGoogleAggregate />
                </When>
            </OverviewSection>
        </When>
    );
};

export const JOB_LIST_INTERSECTION_OBSERVER_NAME =
    'job-list-intersection-observer';

// approximates 5 job cards below the fold
export const JOB_LIST_INTERSECTION_MARGIN = `0px 0px ${180 * 4}px 0px`;

export const {
    context: JobListIntersectionContext,
    Provider: JobListIntersectionProvider,
} = createOrGetIntersectionContext(JOB_LIST_INTERSECTION_OBSERVER_NAME);

const Jobs = () => {
    const postCode = usePostCode();
    const { jobList, similarJobList, hasMoreJobs, fetchNextPage } =
        useContentLists();
    const { isWithin, isBelowBreakpoint, isAboveBreakpoint } = useBreakpoint();

    const { targetRef, reset } = useIntersectionTarget(
        JOB_LIST_INTERSECTION_OBSERVER_NAME,
        () => {
            if (hasMoreJobs && fetchNextPage) {
                fetchNextPage();
            }
        },
        () => void 0,
        {
            rootMargin: JOB_LIST_INTERSECTION_MARGIN,
        }
    );

    // If the intersection observer fires before the job list is loaded, we'll
    // need to reset the intersection observer so that it fires again
    const [isInitialLoad, setIsInitialLoad] = useState(false);
    useEffect(() => {
        if (!isInitialLoad && jobList.length > 0) {
            setIsInitialLoad(true);
            reset();
        }
    }, [jobList, isInitialLoad]);

    return (
        <>
            <When condition={isNotEmpty(jobList || similarJobList)}>
                <OverviewSection
                    style={{ flex: 2 }}
                    title={`Featured projects${
                        postCode ? ' in ' + postCode : ''
                    }`}
                    titleStyle={{
                        position: isWithin('xs', 'sm')
                            ? 'absolute'
                            : 'relative',
                    }}
                >
                    <JobListProvider
                        jobsList={
                            jobList.length < 5 && isBelowBreakpoint('sm')
                                ? [...jobList, ...similarJobList]
                                : jobList
                        }
                    >
                        <JobCards ref={targetRef} />
                    </JobListProvider>
                </OverviewSection>
            </When>
            <When
                condition={
                    jobList.length < MINIMUM_FEATURED_CONTENT &&
                    isAboveBreakpoint('sm')
                }
            >
                <OverviewSection
                    style={{
                        flex: 2,
                    }}
                    title={`${
                        isNotEmpty(jobList) ? 'Additional' : 'Featured'
                    } nearby projects`}
                >
                    <JobListProvider jobsList={similarJobList}>
                        <JobCards ref={targetRef} />
                    </JobListProvider>
                </OverviewSection>
            </When>
        </>
    );
};
